import { CampaignStatus } from '../../models/campaign';
import { UserRole } from '../../models/user';
import { compareDate } from './date';
import { ChannelStatus, PendingStatuses } from '../../models/channel';

export function showChannelStatusDistinction(campaignStatus, sessionRole) {
    return ![CampaignStatus.DRAFT].includes(campaignStatus) && sessionRole !== UserRole.SUPPLIER;
}

export function isPending(status) {
    return PendingStatuses.includes(status);
}

export function checkIfModeExistsInQueryParams(route, mode) {
    let modeExists = false;
    route.queryParams.subscribe(params => {
        if (params && params[mode]) {
            modeExists = params[mode];
        }
    });
    return modeExists;
}

export function checkIfModeExistsInRouteParams(route, mode) {
    return route.snapshot.paramMap.get(mode) == 'true' ? true : false;
}

export function shouldShowExitConfirmationModal(campaignStatus) {
    return [
        CampaignStatus.APPROVED,
        CampaignStatus.PARTIALLY_REJECTED,
        CampaignStatus.PARTIALLY_APPROVED,
        CampaignStatus.PAUSED_PENDING_SUP_APPROVALS,
        CampaignStatus.LIVE,
        CampaignStatus.PARTIALLY_LIVE
    ].includes(campaignStatus);
}

export function isLessThan2WorkingDays(date) {
    let targetDate = new Date(date);
    const today = new Date();
    targetDate = new Date(targetDate.setDate(targetDate.getDate() - 2));
    return compareDate(today, targetDate) == 0 || compareDate(today, targetDate) == 1;
}

export function getProductEndDateByChannelId(campaign, channelId) {
    const product = campaign.products.find(p => p.channels.includes(channelId));
    return product ? product.endDate : undefined;
}

export function buildChannelStatus(channelStatus) {
    switch (channelStatus) {
        case ChannelStatus.PAUSED:
            return 'Auto Paused';
        case ChannelStatus.PENDING_APPROVAL:
        case ChannelStatus.PENDING_REAPPROVAL:
            return 'Pending';
        case ChannelStatus.DRAFT:
            return 'To be submitted';
        default:
            return channelStatus.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
    }
}

export function getChannelSupplierStatus(campaign, channelId): ChannelStatus {
    let channelStatus = ChannelStatus.DRAFT;
    if (campaign.supplierStatus != null) {
        campaign.supplierStatus.forEach(ss => {
            if (ss.channels?.includes(channelId)) {
                channelStatus = ss.status;
            } else if (ss.removedChannels?.includes(channelId)) {
                channelStatus = ChannelStatus.CANCELLED;
            }
        });
    }
    return channelStatus;
}

export function getChannelSupplierStatusForMedia(campaign, media, isMediaOverviewDTO = false) {
    const channelId = isMediaOverviewDTO
        ? media.channel.idChannel
        : media.mediaChannels[0].channelId;
    return getChannelSupplierStatus(campaign, channelId);
}

/**
 * The countries a CPM (per-impression) campaign can be booked in, by ISO code.
 *
 * Keyed on `iso3` and not `idcountry`: the ids are per-environment auto-increment values. The
 * original operational countries happen to share ids across environments because they were seeded
 * together, but anything added later does not — Azerbaijan arrives via a migration that inserts on
 * `iso_3 = 'AZE'` and takes whatever id the sequence is on, so an id check that works here would
 * silently fail in staging or production. The codes come from the `country.iso_3` column as it is
 * actually populated, which is not strictly ISO 3166 ('AE' is two letters, 'KSA' is not a code at
 * all) — match what the data says, not what the standard says.
 *
 * CPM needs an audience model behind it, which is why this is a list rather than "everywhere":
 * per-impression pricing is sold against measured impressions, and only these countries have a
 * population the external provider can measure against.
 */
export const CPM_COUNTRY_ISO3 = ['AE', 'AZE'];

/** True when a CPM campaign can be booked in this country. */
export function isCpmCountry(country): boolean {
    return !!country?.iso3 && CPM_COUNTRY_ISO3.includes(country.iso3);
}

/**
 * True when this country cannot be used with the campaign's billing method — i.e. CPM billing is
 * active and the country is not one of the CPM countries. Per-hour campaigns are unrestricted.
 */
export function isCountryBlockedForBilling(country, billingMethod): boolean {
    return !!billingMethod && billingMethod !== 'PER_HOUR' && !isCpmCountry(country);
}
